import { useState, useEffect, useRef, type FC, type FormEvent } from 'react';
import {
  userFeedbackManager,
  buildMailtoUrl,
  type UserFeedback,
} from '../../utils/userFeedback';

/**
 * FeedbackForm + FeedbackButton
 *
 * Source: feedback-widget module INTEGRATION.md (Stage 6h).
 *
 * Adaptations applied for this app:
 *  - No react-i18next in this codebase → UI strings are inline literals.
 *  - No FocusManager/accessibility util → simple focus-first + Esc-to-close.
 *  - Plain CSS classes (src/styles/feedback.css) using the app's design
 *    tokens instead of Tailwind.
 */

type FeedbackFormState = {
  type: UserFeedback['type'];
  title: string;
  description: string;
  category: string;
  priority: UserFeedback['priority'];
};

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'];
  initialCategory?: string;
  initialError?: string; // pre-fill from an ErrorBoundary catch
}

const FEEDBACK_TYPES: { value: UserFeedback['type']; label: string }[] = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const CATEGORIES = [
  'general',
  'valuation',
  'cap_table',
  'methodology',
  'reporting',
  'compliance',
  'ui_ux',
  'performance',
  'bug',
  'feature_request',
];

const PRIORITIES: { value: UserFeedback['priority']; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const humanize = (c: string): string =>
  c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const FeedbackForm: FC<FeedbackFormProps> = ({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FeedbackFormState>({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError ?? '',
    category: initialCategory,
    priority: 'medium',
  });

  const panelRef = useRef<HTMLDivElement>(null);

  // Reset form state when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      type: initialType,
      title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
      description: initialError ?? '',
      category: initialCategory,
      priority: 'medium',
    });
    setSubmitted(false);
    setFeedbackId(null);
  }, [isOpen, initialType, initialCategory, initialError]);

  // Move focus into the dialog when it opens; restore focus on close.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        'input, textarea, select, button',
      );
      (first ?? panelRef.current)?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      previous?.focus?.();
    };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Hooks must come before any early return.
  if (!isOpen) return null;

  const setField = <K extends keyof FeedbackFormState>(
    field: K,
    value: FeedbackFormState[K],
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="feedback-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} className="feedback-modal" tabIndex={-1}>
        <div className="feedback-body">
          {/* Header */}
          <div className="feedback-header">
            <h2 id="feedback-title" className="feedback-title">
              Send Feedback
            </h2>
            <button
              type="button"
              className="feedback-close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {submitted ? (
            /* Success state */
            <div className="feedback-success">
              <p className="feedback-success-title">Thank you!</p>
              <p className="feedback-success-text">
                Your mail client should have opened. Send the pre-filled email to
                complete submission.
              </p>
              {feedbackId ? <p className="feedback-ref">Ref: {feedbackId}</p> : null}
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="feedback-form">
              {/* Type */}
              <div className="field">
                <label htmlFor="fb-type" className="field-label">
                  Type
                </label>
                <select
                  id="fb-type"
                  className="select"
                  value={formData.type}
                  onChange={(e) =>
                    setField('type', e.target.value as UserFeedback['type'])
                  }
                >
                  {FEEDBACK_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div className="field">
                <label htmlFor="fb-category" className="field-label">
                  Category
                </label>
                <select
                  id="fb-category"
                  className="select"
                  value={formData.category}
                  onChange={(e) => setField('category', e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {humanize(c)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div className="field">
                <span className="field-label">Priority</span>
                <div className="feedback-priority">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={`feedback-priority-btn${
                        formData.priority === p.value ? ' is-active' : ''
                      }`}
                      onClick={() => setField('priority', p.value)}
                      aria-pressed={formData.priority === p.value}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="field">
                <label htmlFor="fb-title" className="field-label">
                  Title *
                </label>
                <input
                  id="fb-title"
                  className="input"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setField('title', e.target.value)}
                  placeholder="Brief summary"
                />
              </div>

              {/* Description */}
              <div className="field">
                <label htmlFor="fb-description" className="field-label">
                  Description *
                </label>
                <textarea
                  id="fb-description"
                  className="textarea"
                  rows={5}
                  required
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Please describe in detail..."
                />
              </div>

              {/* Actions */}
              <div className="feedback-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    isSubmitting ||
                    !formData.title.trim() ||
                    !formData.description.trim()
                  }
                >
                  {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Floating trigger button ----------

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export const FeedbackButton: FC<FeedbackButtonProps> = ({ onOpen }) => (
  <button
    type="button"
    className="feedback-fab"
    onClick={() => onOpen()}
    aria-label="Send feedback"
    title="Send feedback"
  >
    {/* Chat bubble icon */}
    <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  </button>
);
